import { Link } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { MobileNavSection, type NavDropdownItem } from "../NavDropdown";

interface PublicMobileNavBaseProps {
	isOpen: boolean;
	forWhoItems: NavDropdownItem[];
	featuresItems: NavDropdownItem[];
	onClose: () => void;
}

interface LoggedOutMobileNavProps extends PublicMobileNavBaseProps {
	variant: "logged-out";
}

interface LoggedInMobileNavProps extends PublicMobileNavBaseProps {
	variant: "logged-in";
	isSigningOut: boolean;
	onSignOut: () => void;
}

type PublicMobileNavProps = LoggedOutMobileNavProps | LoggedInMobileNavProps;

export function PublicMobileNav(props: PublicMobileNavProps) {
	const { isOpen, forWhoItems, featuresItems, onClose } = props;

	return (
		<AnimatePresence>
			{isOpen && (
				<m.div
					initial={{ opacity: 0, height: 0 }}
					animate={{ opacity: 1, height: "auto" }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
					className="lg:hidden overflow-hidden border-b border-border/40 dark:border-border bg-background/95 backdrop-blur-sm"
				>
					<div className="p-4">
						<nav aria-label="Mobile navigation" className="flex flex-col">
							<MobileNavSection
								label="Solutions"
								items={forWhoItems}
								onItemClick={onClose}
								icon={<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}>groups</span>}
							/>

							<MobileNavSection
								label="Features"
								items={featuresItems}
								onItemClick={onClose}
								icon={<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}>layers</span>}
							/>

							<Link
								to="/pricing"
								onClick={onClose}
								className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-foreground hover:bg-accent transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
							>
								<div className="w-8 h-8 flex items-center justify-center transition-colors" style={{ backgroundColor: "rgba(234,40,4,0.08)" }}>
									{props.variant === "logged-in" ? (
										<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
									) : (
										<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2" }}>attach_money</span>
									)}
								</div>
								Pricing
							</Link>

							<Link
								to="/models"
								onClick={onClose}
								className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-foreground hover:bg-accent transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
							>
								<div className="w-8 h-8 flex items-center justify-center transition-colors" style={{ backgroundColor: "rgba(234,40,4,0.08)" }}>
									<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2", fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
								</div>
								Models
							</Link>

							<Link
								to="/help"
								onClick={onClose}
								className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-foreground hover:bg-accent transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
							>
								<div className="w-8 h-8 flex items-center justify-center transition-colors" style={{ backgroundColor: "rgba(234,40,4,0.08)" }}>
									<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2" }}>description</span>
								</div>
								Docs
							</Link>

							{props.variant === "logged-in" && (
								<>
									<Link
										to="/dashboard"
										onClick={onClose}
										className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-foreground hover:bg-accent transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
									>
										<div className="w-8 h-8 flex items-center justify-center transition-colors" style={{ backgroundColor: "rgba(234,40,4,0.08)" }}>
											<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#6B3FF2" }}>home</span>
										</div>
										Dashboard
									</Link>
									<div className="h-px bg-border my-2" />

									<button
										type="button"
										disabled={props.isSigningOut}
										onClick={() => {
											onClose();
											props.onSignOut();
										}}
										className="flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors w-full text-left group"
										style={{ color: "#ff6e84" }}
										onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
										onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}
										onFocus={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
										onBlur={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}
									>
										<div
											className="w-8 h-8 flex items-center justify-center transition-colors"
											style={{ backgroundColor: "rgba(255,110,132,0.08)" }}
										>
											<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>logout</span>
										</div>
										Sign Out
									</button>
								</>
							)}

							{props.variant === "logged-out" && (
								<>
									<div className="h-px bg-border my-2" />

									<div className="flex items-center gap-2 pt-2">
										<Link
											to="/login"
											onClick={onClose}
											className="flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-bold border border-border text-foreground hover:bg-accent transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
										>
											Log in
										</Link>
										<Link
											to="/signup"
											onClick={onClose}
											className="flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-bold bg-brand-accent text-white hover:bg-brand-accent/90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
										>
											Register
											<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", opacity: "0.7" }}>arrow_forward</span>
										</Link>
									</div>
								</>
							)}
						</nav>
					</div>
				</m.div>
			)}
		</AnimatePresence>
	);
}
